import { ShowDates } from "components/showdatepicker/models";
import moment from "moment";
import React from "react";
import { formatWrittenDay, formatWrittenDayMonth, formatWrittenDayMonthYear, formatWrittenDayMonthYearShort } from "utilities";
import { getRealStartEndDate, getShowTimeDifferenceLabel } from "utilities/showDateUtils";
import ShowTime from "./ShowTime";

interface Props {
  dateTime: server.dto.AppointmentDateTime
}

const ShowDateTime = (props: Props) => {
  const startDisplayMoment = moment.utc(props.dateTime.startDisplay);
  const endDisplayMoment = moment.utc(props.dateTime.endDisplay);
  const { startRealDate } = getRealStartEndDate({
    startDisplayDate: moment.utc(props.dateTime.startDisplay),
    endDisplayDate: moment.utc(props.dateTime.endDisplay),
    timeOfPerformance: props.dateTime.customFallbackTime,
    mode: props.dateTime.mode
  });

  const isSameDay = startDisplayMoment.isSame(endDisplayMoment, "day");

  let timeDifferenceLabel = getShowTimeDifferenceLabel({
    startDisplayDate: startDisplayMoment,
    endDisplayDate: endDisplayMoment,
    timeOfPerformance: props.dateTime.customFallbackTime,
    mode: props.dateTime.mode
  })

  if(timeDifferenceLabel) timeDifferenceLabel = `(${timeDifferenceLabel})`;

  if (props.dateTime.mode === "allDay") {
    if (isSameDay) {
      // Fri, March 1, 2024
      return <>
        <div>
          {formatWrittenDayMonthYear(startDisplayMoment)}
        </div>
        <div>
          <ShowTime dateTime={props.dateTime} />
        </div>
      </>;
    } else {
      // Sun 11 Feb until and inc. Sun 12 Feb (2 days, 1 night)
      return <>
        <div>
          {formatWrittenDayMonth(startDisplayMoment)} {RESX.DateTime.UntilAndIncludingAbbreviated} {formatWrittenDayMonthYearShort(endDisplayMoment)} {timeDifferenceLabel}
        </div>
        <div>
          <ShowTime dateTime={props.dateTime} />
        </div>
      </>;
    }
  }

  if (props.dateTime.mode !== "hasTimeStartsInNight") {
    if (isSameDay) {
      // Fri, 1 March 2024 (1 hour)
      // 09:00-10:00
      return <>
        <div>
          {formatWrittenDayMonthYear(startDisplayMoment)} {timeDifferenceLabel}
        </div>
        <div>
          <ShowTime dateTime={props.dateTime} />
        </div>
      </>;
    } else {
      // Fri, 1 Mar 24 09:00 - Sat, 2 Mar 24 10:00 (1 day, 1 hour)
      // 09:00-10:00
      return <>
        <div>
          {formatWrittenDayMonth(startDisplayMoment)} {RESX.DateTime.UntilAndIncludingAbbreviated} {formatWrittenDayMonthYearShort(endDisplayMoment)} {timeDifferenceLabel}
        </div>
        <div>
          <ShowTime dateTime={props.dateTime} />
        </div>
      </>;
    }
  }

  const realStartAndDisplayIsSameMonth = startRealDate.isSame(startDisplayMoment, "month");

  const inTheNightOfX = realStartAndDisplayIsSameMonth
    ? formatWrittenDay(startDisplayMoment) // Fri, 1
    : formatWrittenDayMonth(startDisplayMoment); // Fri, 1 Mar

  const inTheNightOfY = formatWrittenDayMonthYearShort(startRealDate); // Sat, 2 Mar
  if (isSameDay) {
    // Thu, 29 February 2024 (1 hour)
    // In the night of Thu, 29 Feb - Fri, 1 Mar 24
    // 00:45-01:45

    return <>
      <div>
        {formatWrittenDayMonthYear(startDisplayMoment)} {timeDifferenceLabel}
      </div>
      <div>
        {RESX.DateTime.InTheNightOfXY.replace("{0}", inTheNightOfX).replace("{1}", inTheNightOfY)}
      </div>
      <div>
        <ShowTime dateTime={props.dateTime} />
      </div>
    </>;
  }

  // Fri, 1 Mar 24 00:45 - Mon, 4 Mar 24 01:45 (2 days, 1 hour)
  // In the night of Fri, 1 - Sat, 2 Mar 24
  // 00:45-01:45
  return <>
    <div>
      {formatWrittenDayMonth(startDisplayMoment)} {RESX.DateTime.UntilAndIncludingAbbreviated} {formatWrittenDayMonthYearShort(endDisplayMoment)} {timeDifferenceLabel}
    </div>
    <div>
      {RESX.DateTime.InTheNightOfXY.replace("{0}", inTheNightOfX).replace("{1}", inTheNightOfY)}
    </div>
    <div>
      <ShowTime dateTime={props.dateTime} />
    </div>
  </>;
};

export const ShowDateTimeShowInfo = ({ showDates }: { showDates: ShowDates }) => {
  return <ShowDateTime dateTime={{
    startDisplay: showDates.startDisplayDate.toDate(),
    endDisplay: showDates.endDisplayDate.toDate(),
    mode: showDates.mode,
    customFallbackTime: showDates.timeOfPerformance
  }} />;
}

export default ShowDateTime;